import React from 'react';

/**
 * Read-only renderer for Craft.js builder content
 * Used to display proposals built with the visual builder
 */
export const BuilderContentRenderer = ({ content, isDark = true }) => {
  if (!content) return null;

  let parsedContent;
  try {
    parsedContent = typeof content === 'string' ? JSON.parse(content) : content;
  } catch (err) {
    console.error('Failed to parse builder content:', err);
    return null;
  }

  // Render a single node recursively
  const renderNode = (nodeId, nodes) => {
    const node = nodes[nodeId];
    if (!node) return null;

    const { type, props, nodes: childNodeIds } = node;
    const displayName = type?.resolvedName || type;

    // Render children if any
    const renderChildren = () => {
      if (!childNodeIds || childNodeIds.length === 0) return null;
      return childNodeIds.map(childId => renderNode(childId, nodes));
    };

    // Render based on component type
    // Note: These names match the displayName in each component's .craft config
    switch (displayName) {
      case 'Canvas':
        return (
          <div key={nodeId} className="space-y-4">
            {renderChildren()}
          </div>
        );

      case 'Heading':
        const HeadingTag = props.level || 'h2';
        const headingSizes = {
          h1: 'text-4xl font-bold',
          h2: 'text-3xl font-bold',
          h3: 'text-2xl font-semibold',
        };
        return (
          <HeadingTag
            key={nodeId}
            className={`${headingSizes[props.level]} ${isDark ? 'text-white' : 'text-gray-900'} mb-4`}
            style={{ textAlign: props.textAlign || 'left' }}
          >
            {props.text}
          </HeadingTag>
        );

      case 'Text':
        return (
          <p
            key={nodeId}
            className={`${isDark ? 'text-zinc-200' : 'text-gray-700'} mb-4`}
            style={{
              fontSize: props.fontSize || '16px',
              textAlign: props.textAlign || 'left',
              fontWeight: props.fontWeight || 'normal'
            }}
          >
            {props.text}
          </p>
        );

      case 'Image':
        if (!props.src) return null;
        const imgSrc = props.src.startsWith('http') 
          ? props.src 
          : `${process.env.REACT_APP_BACKEND_URL}${props.src}`;
        const alignmentClasses = {
          left: 'justify-start',
          center: 'justify-center',
          right: 'justify-end'
        };
        return (
          <div key={nodeId} className={`flex ${alignmentClasses[props.alignment] || 'justify-center'} mb-4`}>
            <img
              src={imgSrc}
              alt={props.alt || 'Image'}
              style={{ width: props.width || '100%', maxWidth: '100%' }}
              className="rounded-lg"
            />
          </div>
        );

      case 'Video':
        const getYouTubeId = (url) => {
          if (!url) return null;
          const match = url.match(/(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/watch\?.+&v=))([\w-]{11})/);
          return match ? match[1] : null;
        };
        const videoId = getYouTubeId(props.url);
        if (!videoId) return null;
        return (
          <div key={nodeId} className="flex justify-center mb-4">
            <div style={{ width: props.width || '100%', maxWidth: '100%' }}>
              <div className="relative pb-[56.25%] h-0">
                <iframe
                  src={`https://www.youtube.com/embed/${videoId}`}
                  className="absolute top-0 left-0 w-full h-full rounded-lg"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title="YouTube video"
                />
              </div>
            </div>
          </div>
        );

      case 'Divider':
        const dividerStyles = {
          solid: 'border-t',
          dashed: 'border-t border-dashed',
          dotted: 'border-t border-dotted',
          space: 'border-none'
        };
        return (
          <div
            key={nodeId}
            style={{ padding: `${props.spacing || 16}px 0` }}
          >
            <div className={`${dividerStyles[props.style]} ${isDark ? 'border-zinc-700' : 'border-gray-300'}`} />
          </div>
        );

      case 'Pricing Table':
        // Get the selected billing frequency (default to annual)
        const selectedBilling = props.billingFrequency || 'annual';
        
        // Helper to get item price based on billing
        const getPrice = (item) => {
          if (selectedBilling === 'monthly') {
            return item.monthlyPrice || item.price || 0;
          }
          return item.annualPrice || item.price || 0;
        };
        
        const subtotal = (props.items || []).reduce((sum, item) => {
          if (item.optional && !item.selected) return sum;
          return sum + (getPrice(item) * (item.quantity || 1));
        }, 0);
        const vatAmount = props.chargeVat ? subtotal * (props.vatRate || 0.20) : 0;
        const total = subtotal + vatAmount;

        return (
          <div key={nodeId} className="mb-6">
            {props.title && (
              <h3 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {props.title}
              </h3>
            )}
            
            {/* Billing indicator */}
            {props.showBillingToggle && (
              <div className={`text-sm mb-4 ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>
                Billing: {selectedBilling === 'monthly' ? 'Monthly' : 'Annual'}
              </div>
            )}
            
            <div className={`rounded-lg border overflow-hidden ${isDark ? 'border-zinc-700' : 'border-gray-200'}`}>
              <table className="w-full">
                <thead className={isDark ? 'bg-zinc-800' : 'bg-gray-50'}>
                  <tr>
                    <th className={`text-left p-3 text-sm font-medium ${isDark ? 'text-zinc-300' : 'text-gray-600'}`}>Item</th>
                    {props.showQuantity && (
                      <th className={`text-center p-3 text-sm font-medium w-24 ${isDark ? 'text-zinc-300' : 'text-gray-600'}`}>Qty</th>
                    )}
                    <th className={`text-right p-3 text-sm font-medium w-32 ${isDark ? 'text-zinc-300' : 'text-gray-600'}`}>
                      {selectedBilling === 'monthly' ? 'Monthly' : 'Annual'}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(props.items || []).filter(item => !item.optional || item.selected).map((item, idx) => (
                    <tr key={idx} className={`border-t ${isDark ? 'border-zinc-700' : 'border-gray-200'}`}>
                      <td className="p-3">
                        <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{item.name}</div>
                        {item.description && (
                          <div className={`text-sm ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>{item.description}</div>
                        )}
                      </td>
                      {props.showQuantity && (
                        <td className={`text-center p-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {item.quantity || 1}
                        </td>
                      )}
                      <td className={`text-right p-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {props.currency}{(getPrice(item) * (item.quantity || 1)).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className={`border-t p-4 space-y-2 ${isDark ? 'border-zinc-700 bg-zinc-800/50' : 'border-gray-200 bg-gray-50'}`}>
                <div className="flex justify-between">
                  <span className={isDark ? 'text-zinc-400' : 'text-gray-600'}>Subtotal</span>
                  <span className={isDark ? 'text-white' : 'text-gray-900'}>{props.currency}{subtotal.toLocaleString()}</span>
                </div>
                {props.chargeVat && (
                  <div className="flex justify-between">
                    <span className={isDark ? 'text-zinc-400' : 'text-gray-600'}>VAT ({Math.round((props.vatRate || 0.20) * 100)}%)</span>
                    <span className={isDark ? 'text-white' : 'text-gray-900'}>{props.currency}{vatAmount.toLocaleString()}</span>
                  </div>
                )}
                <div className={`flex justify-between pt-2 border-t ${isDark ? 'border-zinc-700' : 'border-gray-300'}`}>
                  <span className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Total</span>
                  <span className={`text-xl font-bold ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`}>
                    {props.currency}{total.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        );

      case 'Section':
        return (
          <div
            key={nodeId}
            className="mb-4"
            style={{
              backgroundColor: props.backgroundColor || 'transparent',
              padding: `${props.padding || 16}px`,
              borderRadius: `${props.borderRadius || 8}px`
            }}
          >
            {renderChildren()}
          </div>
        );

      case 'Columns':
        const gridCols = {
          2: 'grid-cols-2',
          3: 'grid-cols-3',
          4: 'grid-cols-4'
        };
        return (
          <div
            key={nodeId}
            className={`grid ${gridCols[props.columns] || 'grid-cols-2'} mb-4`}
            style={{ gap: `${props.gap || 16}px` }}
          >
            {renderChildren()}
          </div>
        );

      case 'Column':
        return (
          <div key={nodeId} className="min-h-[50px]">
            {renderChildren()}
          </div>
        );

      default:
        // Unknown block type - try to render children
        return renderChildren();
    }
  };

  // Find root node and render
  const rootNodeId = 'ROOT';
  if (!parsedContent[rootNodeId]) {
    return null;
  }

  return (
    <div className="builder-content-renderer">
      {renderNode(rootNodeId, parsedContent)}
    </div>
  );
};

export default BuilderContentRenderer;
